import { useState } from 'react';
import { IconButton, LockOpenIcon, Text } from '@citibox-panel/ui';
import type { UserSearchHit } from '@citibox-panel/api-client';
import { useNoticeOnError } from '../../search-shell/hooks/useErrorNotice';
import { useUnblockUser } from '../hooks/useUnblockUser';
import { accountSubject } from '../lib/accountSubject';
import { accountTypeClass } from '../lib/accountType';
import { canUnblock } from '../lib/unblockEligibility';
import { AccountActionDialog } from './AccountActionDialog';

// TODO: i18n — copy en ES hasta Fase 1.
const TRIGGER_LABEL = 'Desbloquear';
const DIALOG_LABEL = 'Desbloquear cuenta de mensajero';
// True whether the account was blocked later or was born blocked, as every courier account is —
// and it promises nothing about what the courier's app will let them do.
const BECOMES_ACTIVE = 'La cuenta pasa a estar activa.';
const UNBLOCKED = 'Cuenta desbloqueada';
// The panel's own sentence, resolved from the fact that the write failed and never from what the
// server said: `message` and `detail` are attacker-reachable text. Its own id, so two failures
// refresh one notice and it never replaces the block's or the baja's.
const WRITE_ERROR = 'No se ha podido desbloquear la cuenta. Vuelve a intentarlo.';
const NOTICE_ID = 'unblock-account';

export interface UnblockAccountSectionProps {
  account: UserSearchHit;
  phoneKey: string;
  unblockUser: (id: string, reason: string) => Promise<void>;
  // The unblock completed and the row is about to lose the control that was pressed; only the
  // card knows what is still standing to give the focus to.
  onUnblocked?: () => void;
  onAnnounce?: (message: string) => void;
}

// The «Desbloquear» action on a courier account, on the STATUS ROW like the block it undoes: the
// action lives where its result is read. Only one of the two padlocks is ever on screen, because
// their rules exclude each other.
export function UnblockAccountSection({
  account,
  phoneKey,
  unblockUser,
  onUnblocked,
  onAnnounce,
}: UnblockAccountSectionProps) {
  const [open, setOpen] = useState(false);
  // Called ahead of the gate: this instance re-renders with a status that just flipped, and the
  // Rules of Hooks forbid skipping a call on some renders but not others.
  const mutation = useUnblockUser(
    account.id,
    phoneKey,
    unblockUser,
    accountTypeClass(account.accountType),
  );
  // Floating and not in the modal (ADR 0021): the failure outlives the dialog, which is left
  // exactly as it was, reason included, for the agent to press the button again.
  useNoticeOnError(mutation.isError, NOTICE_ID, WRITE_ERROR, mutation.error);

  if (!canUnblock(account)) return null;

  return (
    <>
      {/* The primary tint, never the block's error one: this lifts a sanction, and the two glyphs
          share a spot, so the tint is half of what tells them apart. `aria-label` is the
          contract. */}
      <IconButton
        aria-label={TRIGGER_LABEL}
        color="primary"
        size="small"
        onClick={() => {
          setOpen(true);
        }}
      >
        <LockOpenIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <AccountActionDialog
        open={open}
        label={DIALOG_LABEL}
        heading={`¿Desbloquear la cuenta de ${accountSubject(account)}?`}
        body={<Text>{BECOMES_ACTIVE}</Text>}
        confirmLabel="Desbloquear"
        confirmColor="primary"
        withReason
        isPending={mutation.isPending}
        onCancel={() => {
          setOpen(false);
        }}
        onConfirm={(reason) => {
          // Never undefined: a dialog with a reason always hands one over.
          if (reason === undefined) return;
          mutation.mutate(reason, {
            // Per-call, so it runs AFTER the hook's own onSuccess has awaited the refetch: the
            // ficha behind the modal already shows the active account when this fires.
            onSuccess: () => {
              setOpen(false);
              onAnnounce?.(UNBLOCKED);
              onUnblocked?.();
            },
          });
        }}
      />
    </>
  );
}
